import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';

/*
  THE VENDOR MAPPING, reading and writing.

  **What this screen is for.** MedEx's two systems between them use 457 different
  spellings for vendors: ClaimSoft 94 and Account Edge 363. The new catalog has
  28 real vendors. Until a spelling points at a vendor, every item under it is
  unmatchable, which on 3 October 2026 was 9,892 items.

  **Nothing here changes what ClaimSoft or Account Edge said.** The raw tables
  keep every spelling exactly as it arrived, for ever. A mapping is a row in
  `vendor_names` saying "this spelling means this vendor", and taking one away
  leaves the underlying data untouched. That was the owner's own question before
  this was built, and it is the thing that makes the screen safe to use.

  The list comes from `vendor_spellings`, a view, because working it out means
  reading 34,763 raw rows and a screen cannot do that on every visit.
*/

/* The states a spelling can be in, which is what the screen groups by. */
export const WAITING = 'waiting';
export const MAPPED = 'mapped';
export const RETIRED = 'retired';

export function stateOf(row) {
  if (row.vendor_id) return MAPPED;
  if (row.looks_retired) return RETIRED;
  return WAITING;
}

export const STATE_WORDS = {
  [MAPPED]:  { label: 'Mapped',     says: 'This spelling points at a vendor, so its items can be matched.' },
  [WAITING]: { label: 'Waiting',    says: 'Nothing has said which vendor this is, so its items cannot be matched yet.' },
  [RETIRED]: { label: 'Retired',    says: 'Your own system marked this spelling dead, with z’s or "do not use". It is flagged rather than mapped, so that decision is not quietly undone.' },
};

export async function loadSpellings() {
  const { data, error } = await fetchAll(() => supabase
    .from('vendor_spellings')
    .select('system, spelling, spelling_key, items, vendor_name_id, vendor_id, vendor_name, looks_retired')
    .order('items', { ascending: false }));
  return { rows: data || [], error };
}

export async function loadVendors() {
  const { data, error } = await supabase.from('vendors').select('id, name').order('name');
  return { vendors: data || [], error };
}

/*
  Point a spelling at a vendor, or take the mapping away.

  **The write is read back**, because a save that no rule allows changes no rows,
  raises nothing and answers "fine". That fault has cost this project a day more
  than once.

  One spelling maps to one vendor, which the database enforces on `spelling_key`,
  so changing a mapping means replacing the row rather than adding a second.
*/
export async function setMapping(row, vendorId) {
  if (row.vendor_name_id) {
    const { error } = await supabase.from('vendor_names').delete().eq('id', row.vendor_name_id);
    if (error) return { ok: false, error };
  }
  if (!vendorId) {
    /* Unmapping. Prove it is gone rather than trusting the delete. */
    if (!row.vendor_name_id) return { ok: true, error: null, vendorId: null };
    const { data, error } = await supabase.from('vendor_names').select('id').eq('id', row.vendor_name_id).maybeSingle();
    if (error) return { ok: false, error };
    return { ok: !data, error: null, vendorId: null };
  }
  const { data, error } = await supabase.from('vendor_names')
    .insert({ vendor_id: vendorId, spelling: row.spelling, seen_in: row.system.replace('_', ' ') })
    .select('id, vendor_id').maybeSingle();
  if (error) return { ok: false, error };
  return { ok: !!data && data.vendor_id === vendorId, error: null, vendorId, vendorNameId: data?.id || null };
}

/* A vendor the catalog does not have yet, added from this screen so a spelling
   has something to point at. The name is the one the owner types, not the
   spelling, because the spelling is how a system wrote it and the name is what
   the company is called. */
export async function addVendor(name) {
  const clean = String(name || '').trim();
  if (!clean) return { ok: false, error: null, vendor: null };
  const { data, error } = await supabase.from('vendors').insert({ name: clean }).select('id, name').maybeSingle();
  if (error) return { ok: false, error, vendor: null };
  return { ok: !!data, error: null, vendor: data || null };
}

/* What the strip above the list counts. Worked out here rather than in the
   screen so the numbers and the filters can never disagree. */
export function tally(rows) {
  const out = { total: rows.length, mapped: 0, waiting: 0, retired: 0, itemsMapped: 0, itemsWaiting: 0 };
  for (const r of rows) {
    const s = stateOf(r);
    out[s === MAPPED ? 'mapped' : s === WAITING ? 'waiting' : 'retired'] += 1;
    if (s === MAPPED) out.itemsMapped += r.items;
    if (s === WAITING) out.itemsWaiting += r.items;
  }
  return out;
}

/*
  THE SAME MAPPING, SEEN FROM THE VENDOR.

  The spelling list is for doing the work. This is for checking it: one row per
  vendor, what ClaimSoft calls them, what Account Edge calls them, and how many
  items hang off each side.

  **It is the only place the ClaimSoft to Account Edge link is visible.** That
  link is produced rather than stored: both systems' spellings point at one
  vendor, so OnHand knows they are the same company without anybody saying so.
  Without this view you could put a ClaimSoft name on the wrong vendor and
  nothing on screen would tell you.

  **`in_one_system_only` is the signal worth having.** A vendor named in Account
  Edge and never in ClaimSoft is either genuinely one-sided or a sign that a
  ClaimSoft spelling is still unmapped. On the day it was built it caught
  Ottobock: mapped on the Account Edge side, while ClaimSoft's "OTTO BOCK no CPM
  product" and its 120 items sat waiting.
*/
export async function loadByVendor() {
  const { data, error } = await fetchAll(() => supabase
    .from('vendor_mapping_by_vendor')
    .select('vendor_id, vendor_name, claimsoft_spellings, account_edge_spellings, claimsoft_items, account_edge_items, items, products_in_the_catalog, in_one_system_only')
    .order('items', { ascending: false }));
  return { rows: data || [], error };
}
